import React from 'react';
import PropTypes from 'prop-types';
import {callAction} from 'client/framework';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import TextInput from 'generic/Forms/TextInput/TextInput.react';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import {
  addToast,
  Anchor,
  Banner,
  ButtonGroup,
  Button,
  Dropdown,
  Icon,
  DropdownItem,
  Modal,
  Dialogue,
  Text,
  removeToast
} from '@albert-io/atomic';

import BlockyDropdown from 'generic/BlockyDropdown/BlockyDropdown.react';
import CourseDetail from 'client/GoogleClassroom/Courses/CourseDetail.react';

import ClassroomCodeRegenerator from './ClassroomCodeRegenerator/ClassroomCodeRegenerator.react';
import teacherClassroomSettingsActions from './TeacherClassroomSettingsActions';
import teacherClassroomSettingsStore from './TeacherClassroomSettingsStore';

import './teacher-classroom-settings.scss';

export default awaitMandarkQueries(
  () => ({
    queries: {
      classroom: teacherClassroomSettingsStore.getClassroomQuery()
    }
  }),
  class TeacherClassroomSettings extends React.Component {
    static propTypes = {
      classroom: PropTypes.instanceOf(ClassroomModelV1).isRequired
    };

    constructor(props) {
      super(props);
      this.state = {
        modal: null,
        onModalConfirm: null
      };
    }

    componentDidMount() {
      this.initializeSettings();
      callAction(
        teacherClassroomSettingsActions.SET_SELECTED_SUBJECTS,
        this.props.classroom.getSubjects()
      );
    }

    componentDidUpdate() {
      this.initializeSettings();
      this.showToasts();
    }

    componentWillUnmount() {
      this.showToasts();
      callAction(teacherClassroomSettingsActions.RESET_STORE);
    }

    initializeSettings() {
      if (!teacherClassroomSettingsStore.isInitialized()) {
        const classroom = teacherClassroomSettingsStore.getClassroom();
        callAction(teacherClassroomSettingsActions.INITIALIZE_SETTINGS, classroom.getName());
      }
    }

    showToasts() {
      if (teacherClassroomSettingsStore.getSuccessMessaging()) {
        addToast({
          message: teacherClassroomSettingsStore.getSuccessMessaging(),
          title: 'Success',
          type: 'success'
        });
        callAction(teacherClassroomSettingsActions.MESSAGE_SHOWN);
      } else if (teacherClassroomSettingsStore.getErrorMessaging()) {
        addToast({
          message: teacherClassroomSettingsStore.getErrorMessaging(),
          title: 'Error',
          type: 'danger'
        });
        callAction(teacherClassroomSettingsActions.MESSAGE_SHOWN);
      }
    }

    launchArchiveModal = () => {
      this.setState({
        modal: ArchiveClassroomModal,
        onModalConfirm: this.archiveModalConfirm
      });
    };

    launchUnarchiveModal = () => {
      this.setState({
        modal: UnarchiveClassroomModal,
        onModalConfirm: this.unarchiveModalConfirm
      });
    };

    launchDeleteModal = () => {
      this.setState({
        modal: DeleteClassroomModal,
        onModalConfirm: this.deleteModalConfirm
      });
    };

    launchSelfRemovalModal = () => {
      this.setState({
        modal: SelfRemovalModal,
        onModalConfirm: this.removeSelfConfirm
      });
    };

    archiveModalConfirm() {
      callAction(teacherClassroomSettingsActions.ARCHIVE_CLASSROOM);
    }

    unarchiveModalConfirm() {
      // Remove permanent archived toast
      removeToast(teacherClassroomSettingsStore.getClassroom().getId());
      callAction(teacherClassroomSettingsActions.UNARCHIVE_CLASSROOM);
    }

    deleteModalConfirm() {
      callAction(teacherClassroomSettingsActions.DELETE_CLASSROOM);
    }

    removeSelfConfirm() {
      callAction(teacherClassroomSettingsActions.REMOVE_SELF_FROM_CLASSROOM);
    }

    saveClassroom() {
      callAction(teacherClassroomSettingsActions.SAVE_CLASSROOM);
    }

    generateGoogleClassroomDetails() {
      return (
        <CourseDetail courseId={teacherClassroomSettingsStore.getClassroom().getGoogleCourseId()} />
      );
    }

    render() {
      if (!teacherClassroomSettingsStore.hasSchoolData()) {
        return <LoadingIndicator />;
      }

      const associatedSchool = teacherClassroomSettingsStore.getCurrentSchool();

      const classroom = teacherClassroomSettingsStore.getClassroom();
      const schoolsList = teacherClassroomSettingsStore.getAllAssociatedSchools();
      const isArchived = teacherClassroomSettingsStore.getClassroom().isArchived();
      const {modal: ModalToShow, onModalConfirm} = this.state;

      return (
        <div className='teacher-classroom-settings card'>
          <TextInput
            className='teacher-classroom-settings__input'
            error={teacherClassroomSettingsStore.hasNameError()}
            errorMessage={teacherClassroomSettingsStore.getNameError()}
            label='Class Name'
            onChange={(e) =>
              callAction(teacherClassroomSettingsActions.SET_CLASS_NAME, e.target.value)
            }
            value={teacherClassroomSettingsStore.getClassroomName()}
          />
          <BlockyDropdown
            className='teacher-classroom-settings__input teacher-classroom-settings__input--narrow'
            data={schoolsList}
            disabled={!teacherClassroomSettingsStore.isClassroomSchoolEditable()}
            defaultValue={associatedSchool}
            displayProperty='name'
            label='School'
            storeName={teacherClassroomSettingsStore.getSchoolSelectionDropdownStore().getName()}
            onChange={() => {
              callAction(teacherClassroomSettingsActions.SCHOOL_ASSOCIATION_CHANGE);
            }}
          />
          {teacherClassroomSettingsStore.hasUnapprovedCoteacherError() && (
            <Banner
              theme='danger'
              className='u-mar-b_2 teacher-classroom-settings__input-error-banner'
            >
              <Banner.Icon icon='exclamation' />
              <div>
                School association was not updated. All co-teachers must be approved by the
                school.&nbsp;
                <Anchor
                  href='https://help.albert.io/en/articles/2459978-co-teacher-classrooms'
                  target='_blank'
                  rel='noopener noreferrer'
                  as='a'
                  inheritColor
                  underlined
                >
                  Learn more
                </Anchor>
                .
              </div>
            </Banner>
          )}
          {classroom.getGoogleCourseId() ? this.generateGoogleClassroomDetails() : null}
          <ClassroomCodeRegenerator classroom={classroom} />
          <ButtonGroup align='right'>
            <Dropdown
              position='bottom-start'
              positionFixed
              trigger={
                <Button variant='outlined' data-testid='settings-dropdown-actions'>
                  Options
                  <Icon className='u-mar-l_1' icon='caret-down' />
                </Button>
              }
            >
              <DropdownItem
                withBottomBorder
                color='secondary'
                data-testid='settings-archive-classroom'
                onClick={isArchived ? this.launchUnarchiveModal : this.launchArchiveModal}
              >
                {isArchived ? 'Unarchive class' : 'Archive class'}
              </DropdownItem>
              {classroom.getTeachers().size > 1 && (
                <DropdownItem
                  withBottomBorder
                  color='red'
                  variant='text'
                  data-testid='settings-remove-self'
                  onClick={this.launchSelfRemovalModal}
                >
                  Remove Me from This Classroom
                </DropdownItem>
              )}
              {classroom.getTeachers().size === 1 && (
                <DropdownItem
                  withBottomBorder
                  color='red'
                  variant='text'
                  data-testid='settings-delete-classroom'
                  onClick={this.launchDeleteModal}
                >
                  Delete Classroom
                </DropdownItem>
              )}
            </Dropdown>
            <Button onClick={this.saveClassroom}>Save</Button>
          </ButtonGroup>
          {ModalToShow && (
            <ModalToShow
              onConfirm={onModalConfirm}
              onDismiss={() => this.setState({modal: null, onModalConfirm: null})}
              classroomName={classroom.getName()}
            />
          )}
        </div>
      );
    }
  }
);

const modalPropTypes = {
  onConfirm: PropTypes.func,
  onDismiss: PropTypes.func,
  classroomName: PropTypes.string
};

const ArchiveClassroomModal = ({onConfirm, onDismiss, classroomName}) => {
  return (
    <Modal ariaLabel='Archive Classroom' handleClose={onDismiss}>
      {({modalContentStyle, CloseButtonWrapper}) => (
        <Dialogue inModal hideCloseBtn className={modalContentStyle}>
          <Dialogue.Title align='center'>
            <div className='action-modal__heading-icon action-modal__heading-icon-info'>
              <Icon icon={['fal', 'archive']} color='inherit' />
            </div>
          </Dialogue.Title>
          <Dialogue.Body>
            <Text
              bold
              className='u-display_flex u-flex-direction_column u-text-align_center u-mar-b_3'
            >
              Are you sure you want to archive <div>&quot;{classroomName}&quot;?</div>
            </Text>
            <ul className='u-mar-b_4 u-pad-lr_10'>
              <li className='u-mar-b_2'>
                Classroom statistics will still remain available to you.
              </li>
              <li className='u-mar-b_2'>
                Questions in &quot;active&quot; assignments will remain blocked for students.
              </li>
              <li className='u-mar-b_2'>Students and co-teachers will no longer have access.</li>
            </ul>
          </Dialogue.Body>
          <Button
            variant='outlined'
            onClick={() => {
              onConfirm();
              onDismiss();
            }}
            className='u-display_block u-width_100pc u-mar-b_1'
            data-testid='settings-modal-archive-classroom'
          >
            Archive Classroom
          </Button>
          <CloseButtonWrapper>
            <Button variant='outlined' color='secondary' className='u-display_block u-width_100pc'>
              Cancel
            </Button>
          </CloseButtonWrapper>
        </Dialogue>
      )}
    </Modal>
  );
};
ArchiveClassroomModal.propTypes = modalPropTypes;

const UnarchiveClassroomModal = ({onConfirm, onDismiss, classroomName}) => {
  return (
    <Modal ariaLabel='Unarchive Classroom' handleClose={onDismiss}>
      {({modalContentStyle, CloseButtonWrapper}) => (
        <Dialogue inModal hideCloseBtn className={modalContentStyle}>
          <Dialogue.Title align='center'>
            <div className='action-modal__heading-icon action-modal__heading-icon-info'>
              <Icon icon={['fal', 'archive']} color='inherit' />
            </div>
            <Text bold>{`Unarchive "${classroomName}"?`}</Text>
          </Dialogue.Title>
          <Dialogue.Body className='u-text-align_center u-mar-b_5'>
            Unarchiving a class will make it accessible to students and any co-teachers.
          </Dialogue.Body>
          <CloseButtonWrapper>
            <Button
              variant='outlined'
              color='secondary'
              className='u-display_block u-width_100pc u-mar-b_1 u-mar-t_2'
            >
              Cancel
            </Button>
          </CloseButtonWrapper>
          <Button
            variant='outlined'
            onClick={() => {
              onConfirm();
              onDismiss();
            }}
            className='u-display_block u-width_100pc'
            data-testid='settings-modal-unarchive-classroom'
          >
            Unarchive Classroom
          </Button>
        </Dialogue>
      )}
    </Modal>
  );
};
UnarchiveClassroomModal.propTypes = modalPropTypes;

const DeleteClassroomModal = ({onConfirm, onDismiss, classroomName}) => (
  <Modal arialLabel='Delete Classroom' handleClose={onDismiss}>
    {({modalContentStyle, CloseButtonWrapper}) => (
      <Dialogue inModal hideCloseBtn className={modalContentStyle} size='s'>
        <Dialogue.Title align='center'>
          <div className='action-modal__heading-icon'>
            <Icon icon={['fal', 'octagon-exclamation']} color='danger' />
          </div>
        </Dialogue.Title>
        <Dialogue.Body>
          <Text
            bold
            className='u-display_flex u-flex-direction_column u-text-align_center u-mar-b_5'
          >
            Are you sure you want to delete <div>&quot;{classroomName}&quot;?</div>
          </Text>
        </Dialogue.Body>
        <CloseButtonWrapper>
          <Button
            variant='outlined'
            color='secondary'
            className='u-display_block u-width_100pc u-mar-t_2 u-mar-b_1'
          >
            Cancel
          </Button>
        </CloseButtonWrapper>
        <Button
          variant='outlined'
          color='destructive'
          onClick={onConfirm}
          className='u-display_block u-width_100pc'
          data-testid='settings-modal-delete-classroom'
        >
          Delete Classroom
        </Button>
      </Dialogue>
    )}
  </Modal>
);
DeleteClassroomModal.propTypes = modalPropTypes;

const SelfRemovalModal = ({onConfirm, onDismiss, classroomName}) => (
  <Modal ariaLabel='Remove yourself from Classroom' handleClose={onDismiss}>
    {({modalContentStyle, CloseButtonWrapper}) => (
      <Dialogue inModal hideCloseBtn className={modalContentStyle}>
        <Dialogue.Title align='center'>
          <div className='action-modal__heading-icon'>
            <Icon icon={['fal', 'octagon-exclamation']} color='danger' />
          </div>
        </Dialogue.Title>
        <Dialogue.Body align='center'>
          Are you sure you want to remove yourself from &quot;{classroomName}&quot;?
        </Dialogue.Body>
        <CloseButtonWrapper>
          <Button
            variant='outlined'
            color='secondary'
            className='u-display_block u-width_100pc u-mar-t_2 u-mar-b_1'
          >
            Cancel
          </Button>
        </CloseButtonWrapper>
        <Button
          variant='outlined'
          color='destructive'
          onClick={onConfirm}
          className='u-display_block u-width_100pc'
          data-testid='settings-modal-remove-self'
        >
          Remove Me From This Classroom
        </Button>
      </Dialogue>
    )}
  </Modal>
);
SelfRemovalModal.propTypes = modalPropTypes;
